/**
 * kp-carousel.js
 *
 * Vanilla JS controller for KP product carousels rendered by
 * snippets/kp-product-carousel.liquid.
 *
 * Responsibilities:
 *   - Wire prev/next buttons to scroll the track to the next page's
 *     leading slide's offsetLeft (the real CSS scroll-snap target).
 *   - Update the "N / M" counter when the user scrolls or autoplay ticks.
 *   - Auto-advance every N ms when data-kp-autoplay="true", with pause
 *     on hover/focus/visibility change. Respects prefers-reduced-motion.
 *   - Recompute slides-per-page when the viewport resizes.
 *
 * Notes:
 *   - All scroll advances target slides[idx].offsetLeft directly. No
 *     scrollBy(clientWidth), no scroll-snap correction dance. This is
 *     the root-cause fix for the recurring edge-clipping bug.
 *   - The outer .kp-carousel container clips any sub-pixel drift, so
 *     even if scrollLeft lands a couple pixels off, nothing bleeds.
 */
(function () {
  'use strict';

  var DEFAULT_INTERVAL_MS = 5000;
  var MIN_INTERVAL_MS = 2000;
  var SCROLL_DEBOUNCE_MS = 150;

  var prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  var supportsScrollEnd = 'onscrollend' in window;

  function readInterval(el) {
    var raw = parseInt(el.getAttribute('data-kp-interval'), 10);
    if (!isFinite(raw) || raw < MIN_INTERVAL_MS) return DEFAULT_INTERVAL_MS;
    return raw;
  }

  function attach(carousel) {
    if (carousel.__kpCarouselBound) return;
    carousel.__kpCarouselBound = true;

    var track = carousel.querySelector('[data-kp-track]');
    var prevBtn = carousel.querySelector('[data-kp-prev]');
    var nextBtn = carousel.querySelector('[data-kp-next]');
    var pageEl = carousel.querySelector('[data-kp-page]');
    var totalEl = carousel.querySelector('[data-kp-total]');
    if (!track || !prevBtn || !nextBtn) return;

    var slides = track.querySelectorAll('.kp-carousel__slide');
    if (!slides.length) return;

    var slidesPerPage = 1;
    var totalPages = 1;
    var autoplayEnabled = carousel.getAttribute('data-kp-autoplay') === 'true' && !prefersReducedMotion;
    var intervalMs = readInterval(carousel);
    var timerId = null;
    var hovered = false;
    var focused = false;
    var scrollDebounceId = null;

    function getColumnGap() {
      var cs = window.getComputedStyle(track);
      var raw = parseFloat(cs.columnGap || cs.gap || '0');
      return isFinite(raw) ? raw : 0;
    }

    function recomputePages() {
      slides = track.querySelectorAll('.kp-carousel__slide');
      if (!slides.length) {
        slidesPerPage = 1;
        totalPages = 1;
        return;
      }
      var slideW = slides[0].offsetWidth;
      var gap = getColumnGap();
      // slides + (slides - 1) gaps must fit in viewport.
      // pages-per-viewport = floor((clientWidth + gap) / (slideW + gap))
      slidesPerPage = Math.max(
        1,
        Math.floor((track.clientWidth + gap) / (slideW + gap))
      );
      totalPages = Math.max(1, Math.ceil(slides.length / slidesPerPage));
      if (totalEl) totalEl.textContent = totalPages;
      updateCounter();
    }

    function currentPage() {
      if (!slides.length) return 1;
      var maxScroll = track.scrollWidth - track.clientWidth;
      // If we're at (or very close to) the end, we're on the last page —
      // even if the end-clamped scrollLeft doesn't divide evenly by stride.
      if (track.scrollLeft >= maxScroll - 4) return totalPages;
      var slideW = slides[0].offsetWidth;
      var gap = getColumnGap();
      var stride = slideW + gap;
      if (stride <= 0) return 1;
      var firstIdx = Math.round(track.scrollLeft / stride);
      if (firstIdx < 0) firstIdx = 0;
      if (firstIdx > slides.length - 1) firstIdx = slides.length - 1;
      return Math.floor(firstIdx / slidesPerPage) + 1;
    }

    function updateCounter() {
      if (pageEl) pageEl.textContent = currentPage();
    }

    function scrollToSlide(idx) {
      if (idx < 0) idx = 0;
      if (idx > slides.length - 1) idx = slides.length - 1;
      track.scrollTo({
        left: slides[idx].offsetLeft,
        behavior: prefersReducedMotion ? 'auto' : 'smooth'
      });
    }

    function advance(direction) {
      var maxScroll = track.scrollWidth - track.clientWidth;
      var page = currentPage();
      var firstIdxOnCurrentPage = (page - 1) * slidesPerPage;
      var targetIdx = firstIdxOnCurrentPage + direction * slidesPerPage;
      var smooth = prefersReducedMotion ? 'auto' : 'smooth';

      if (direction > 0) {
        // Already showing the end? Wrap to start.
        if (track.scrollLeft >= maxScroll - 4) {
          track.scrollTo({ left: 0, behavior: smooth });
          return;
        }
        // Target the next page's first slide, but clamp at maxScroll so
        // the final partial page (e.g. 17 slides at 4-up) is still shown
        // flush-right instead of getting skipped.
        var fwdLeft = targetIdx < slides.length
          ? Math.min(slides[targetIdx].offsetLeft, maxScroll)
          : maxScroll;
        track.scrollTo({ left: fwdLeft, behavior: smooth });
        return;
      }

      // direction < 0: prev
      if (track.scrollLeft <= 4) {
        // Already at the start → wrap to the last page (flush-right).
        track.scrollTo({ left: maxScroll, behavior: smooth });
        return;
      }
      if (targetIdx < 0) targetIdx = 0;
      track.scrollTo({ left: slides[targetIdx].offsetLeft, behavior: smooth });
    }

    function tick() {
      if (hovered || focused) return;
      if (document.visibilityState && document.visibilityState !== 'visible') return;
      advance(1);
    }

    function startAutoplay() {
      if (!autoplayEnabled) return;
      stopAutoplay();
      timerId = window.setInterval(tick, intervalMs);
    }

    function stopAutoplay() {
      if (timerId !== null) {
        window.clearInterval(timerId);
        timerId = null;
      }
    }

    // ---- Wire events ----
    prevBtn.addEventListener('click', function () {
      advance(-1);
    });
    nextBtn.addEventListener('click', function () {
      advance(1);
    });

    if (supportsScrollEnd) {
      track.addEventListener('scrollend', updateCounter);
    } else {
      track.addEventListener('scroll', function () {
        if (scrollDebounceId !== null) window.clearTimeout(scrollDebounceId);
        scrollDebounceId = window.setTimeout(updateCounter, SCROLL_DEBOUNCE_MS);
      });
    }

    carousel.addEventListener('pointerenter', function () {
      hovered = true;
    });
    carousel.addEventListener('pointerleave', function () {
      hovered = false;
    });
    carousel.addEventListener('focusin', function () {
      focused = true;
    });
    carousel.addEventListener('focusout', function (event) {
      if (!carousel.contains(event.relatedTarget)) focused = false;
    });

    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') {
        startAutoplay();
      } else {
        stopAutoplay();
      }
    });

    if (typeof ResizeObserver === 'function') {
      var ro = new ResizeObserver(function () {
        recomputePages();
      });
      ro.observe(track);
    } else {
      window.addEventListener('resize', recomputePages);
    }

    // ---- Init ----
    recomputePages();
    startAutoplay();
  }

  function attachAll(root) {
    var scope = root || document;
    var nodes = scope.querySelectorAll('[data-kp-carousel]');
    for (var i = 0; i < nodes.length; i++) attach(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      attachAll(document);
    });
  } else {
    attachAll(document);
  }

  // Re-scan when sections are re-rendered by the Shopify theme editor.
  document.addEventListener('shopify:section:load', function (event) {
    attachAll(event.target);
  });
})();
